import { ReactNode, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { GymInfo } from "./header/GymInfo";
import { TrialCountdown } from "./header/TrialCountdown";
import { NotificationBell } from "./header/NotificationBell";
import { ChatIcon } from "@/components/header/ChatIcon";
import { usePortalMessagesUnreadCount } from "@/hooks/portalMessages/usePortalMessages";
import { useAuth } from "@/contexts/AuthContext";
import { AnnounceModal } from "@/components/referral/AnnounceModal";
import { TermsModal } from "@/components/referral/TermsModal";
import { useRenewalReminder } from "@/hooks/subscription/useRenewalReminder";
import { RenewalBanner } from "@/components/renewal/RenewalBanner";
import { RenewalPill } from "@/components/renewal/RenewalPill";
import { RenewalReminderModal } from "@/components/renewal/RenewalReminderModal";
import { RenewalFinalDay } from "@/components/renewal/RenewalFinalDay";

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { isAuthenticated, isAuthReady } = useAuth();
  const { data } = usePortalMessagesUnreadCount(isAuthReady && isAuthenticated);
  const unreadCount = data?.unreadCount ?? 0;
  const [termsOpen, setTermsOpen] = useState(false);
  const renewalReminder = useRenewalReminder();

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full overflow-x-hidden bg-background">
        <AppSidebar onOpenReferral={() => setTermsOpen(true)} />
        <AnnounceModal
          onLearnMore={() => setTermsOpen(true)}
          suppressed={Boolean(renewalReminder.timing)}
        />
        <RenewalReminderModal reminder={renewalReminder} />
        <RenewalFinalDay reminder={renewalReminder} />
        <TermsModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/70">
            <div className="flex min-w-0 items-center justify-between gap-2 px-3 py-2.5 sm:gap-4 sm:px-6 sm:py-3">
              <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                  <SidebarTrigger className="shrink-0 text-muted-foreground" />
                  <div className="hidden h-8 w-px bg-border sm:block" />
                </div>
                <div className="min-w-0 flex-1">
                  <GymInfo />
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-end gap-1">
                <div className="flex shrink-0 items-center gap-1">
                  <ChatIcon unreadCount={unreadCount} />
                  <NotificationBell />
                </div>
                <TrialCountdown />
                <RenewalPill reminder={renewalReminder} />
              </div>
            </div>
          </header>
          <main className="min-w-0 flex-1 px-4 py-4 sm:p-6">
            <RenewalBanner reminder={renewalReminder} />
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
